import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as actions from "@/store/slices/operator.slice"; // Import your action creators
import * as api from "@/services/operator"; // Import your API functions
import { PayloadAction } from "@reduxjs/toolkit";
import { isBoolean } from "lodash";
import { IFetchOperators } from "@/services/operator/operator.type";
import { isSameRequestParams } from "@/utils/requestParamsChecker";

// Define action types
export interface GetOperatorAction extends PayloadAction<number> {
  type: "operator/getOperator";
}

export interface GetOperatorsAction extends PayloadAction<number> {
  type: "operator/getOperators";
}
export interface GetOperatorsListAction extends PayloadAction<IFetchOperators> {
  type: "operator/getOperatorsList";
}

export interface CreateOperatorAction extends PayloadAction<Operator> {
  type: "operator/createOperator";
}

export interface UpdateOperatorAction extends PayloadAction<Operator> {
  type: "operator/updateOperator";
}

export interface DeleteOperatorAction extends PayloadAction<DeleteOperator> {
  type: "operator/deleteOperator";
}

export interface ActivateOperatorAction extends PayloadAction<Operator> {
  type: "operator/activateOperator";
}

// Define the shape of the API response for a single Operator
export interface SingleOperatorResponse {
  name: string;
  operatorId: string;
  sourceId: string;
  providerId: string;
  subapplicationId: string;
  outsideReferenceId: string;
  isDeleted: boolean;
  createdOn: string;
  updatedOn: string;
  provider: ProviderProps;
  subapplication: SubapplicationProps;
}

type ProviderProps = {
  providerId: string;
  name: string;
};

type SubapplicationProps = {
  id: string;
  name: string;
};

export interface Operator {
  name: string;
  operatorId: string;
  sourceId: string;
  providerId: string;
  subapplicationId: string;
  outsideReferenceId: string;
  isDeleted: boolean;
  createdOn: string;
  updatedOn: string;
  provider: ProviderProps;
  subapplication: SubapplicationProps;
}
export interface IOperatorListItem {
  name: string;
  sourceId: string;
  operatorId: string;
}


export interface ICreateOperatorProps {
  sourceId: string;
  name: string;
  providerId: string;
  subapplicationId: string;
}

export interface IUpdateOperatorProps {
  sourceId: string;
  name: string;
  providerId: string;
  subapplicationId: string;
}


export interface DeleteOperator {
  providerId: string;
}

export interface Pagination {
  totalRecords: number;
  currentPage: number;
  totalPages: number;
  nextPage: number;
  previousPage: number;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data: Operator[];
  pagination: Pagination;
}
export interface AllOperatorsListApiResponse {
  success: boolean;
  message: string;
  data: IOperatorListItem[];
  pagination: Pagination;
}

function* getOperators(action: GetOperatorsAction): Generator {
  try {
    const response: any = yield call(api.fetchOperators as any, action?.payload);
    if (isValidOperatorsResponse(response?.data)) {
      yield put(actions.getOperatorsSuccess(response?.data));
    } else {
      yield put(actions.getOperatorsFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getOperatorsFailure(error?.response?.data || { message: error?.message }));
  }
}

// Your saga function
function* getOperator(action: GetOperatorAction): Generator {
  try {
    const response: any = yield call(api.fetchOperator as any, action?.payload);
    const res = response?.data?.data;
    if (isValidOperatorResponse(res)) {
      yield put({
        type: "operator/getOperatorSuccess",
        payload: res as SingleOperatorResponse,
      });
    } else {
      yield put({ type: "operator/getOperatorFailure", payload: response?.data });
    }
  } catch (error: any) {
    yield put({ type: "operator/getOperatorFailure", payload: error?.response?.data || { message: (error as any)?.message } });
  }
}

function* createOperator(action: CreateOperatorAction): Generator {
  try {
    const response: any = yield call(api.createOperator, action?.payload);
    const responseData = response?.data;
    if (responseData?.success) {
      yield put({
        type: "operator/createOperatorSuccess",
        payload: responseData as SingleOperatorResponse,
      });
    } else {
      yield put(actions.createOperatorFailure(responseData));
    }
  } catch (error: any) {
    yield put(actions.createOperatorFailure(error?.response?.data || { message: error?.message }));
  }
}

function* updateOperator(action: any): Generator {
  try {
    const { id, updatePayload } = action.payload;
    const response: any = yield call(
      api.updateOperator,
      id,
      updatePayload
    );

    if (response?.data?.success || response?.status === 200) {
      yield put(actions.updateOperatorSuccess(response.data));
    } else {
      yield put(actions.updateOperatorFailure(response.data));
    }
  } catch (error: any) {
    yield put(actions.updateOperatorFailure(error?.response?.data || { message: error?.message }));
  }
}

function* deleteOperator(action: DeleteOperatorAction): Generator {
  try {
    const response: any = yield call(api.deleteOperator, action?.payload);
    const responseData = response?.data;
    if (responseData?.success) {
      yield put(actions.deleteOperatorSuccess(responseData));
    } else {
      yield put(actions.deleteOperatorFailure(responseData));
    }
  } catch (error: any) {
    yield put(actions.deleteOperatorFailure(error?.response?.data || { message: (error as any)?.message }));
  }
}

function* activateOperator(action: ActivateOperatorAction): Generator {
  try {
    const { operatorId } = action.payload;
    const response: any = yield call(api.activateOperator, operatorId);
    const responseData = response?.data;
    if (responseData?.success || response?.status === 200) {
      yield put(actions.activateOperatorSuccess(responseData));
    } else {
      yield put(actions.activateOperatorFailure(responseData));
    }
  } catch (error: any) {
    yield put(actions.activateOperatorFailure(error?.response?.data || { message: error?.message }));
  }
}

function isValidOperatorResponse(
  response: unknown
): response is SingleOperatorResponse {
  return (
    typeof response === "object" &&
    response !== null &&
    "providerId" in response &&
    "operatorId" in response &&
    "name" in response &&
    "sourceId" in response &&
    "createdOn" in response &&
    "updatedOn" in response
    // Add other properties if present in the API response
  );
}

function isValidOperatorsResponse(response: ApiResponse): boolean {
  return (
    isBoolean(response.success) ||
    response.message === "Succesfully grabbed operators."
  );
}
function* getAllOperatorsList(action: PayloadAction<IFetchOperators>): Generator {
  try {
    if (isSameRequestParams(action.payload)) {
      return;
    }
    const response: any = yield call(api.fetchAllOperatorsList, action.payload);
    if (isValidOperatorsResponse(response?.data) || response?.data?.success) {
      yield put(actions.getOperatorsListSuccess({
        ...response.data,
        providerId: action.payload.providerId, // Pass providerId from request
      }));
    } else {
      yield put(actions.getOperatorsListFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getOperatorsListFailure(error?.response?.data));
  }
}

function* operatorSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest<GetOperatorsAction>("operator/getOperators", getOperators);
  yield takeLatest<GetOperatorsListAction>(actions.getOperatorsList.type, getAllOperatorsList);
  yield takeEvery<GetOperatorAction>("operator/getOperator", getOperator);
  yield takeEvery<CreateOperatorAction>(
    "operator/createOperator",
    createOperator
  );
  yield takeEvery<UpdateOperatorAction>(
    "operator/updateOperator",
    updateOperator
  );
  yield takeEvery<DeleteOperatorAction>(
    "operator/deleteOperator",
    deleteOperator
  );
  yield takeEvery<ActivateOperatorAction>(
    actions.activateOperator.type,
    activateOperator
  );
}

export default operatorSaga;